import { useClientPortalAPI, useQueryParams } from "@certn/api";
import { Text, Stack, Link } from "@certn/cascadia";
import {
  Container,
  DangerTag,
  DataTable,
  DefaultTag,
  Icon,
  Loader,
  MediaQuery,
  Pagination,
  SuccessTag,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { InvoicesActionsDropdownMenu } from "../InvoicesActionsDropdownMenu";
import { TableDataCard } from "../components/TableDataCard";
import { DateRangeFilter } from "../filters/DateRangeFilter";
import { InvoiceStatusFilter } from "../filters/InvoiceStatusFilter";
import { ProfileNameFilter } from "../filters/ProfileNameFilter";
import { ClearFilters } from "../../../components/filters/ClearFilters";
import { useCanViewClientPortalPricing } from "../../../components/permissions/Permissions";
import { INVOICE_STATUS } from "../../../constants";
import { useClientAccount } from "../../../hooks/useClientAccount";
import { formatNullableCurrency } from "../../../utils/formatNullableCurrency";
import { Schemas } from "../../../types";

type Invoice = Schemas["BillingProfileInvoiceList"];

export function CurrentInvoicesTab() {
  const { client } = useClientAccount();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const { setParam, flatParams } = useQueryParams();
  const [profileNameOptions, setProfileNameOptions] = useState([
    { id: "", name: "" },
  ]);

  const { data, isLoading } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/${client?.id}/billing-profile-invoices/`,
    {
      page: flatParams.page,
      page_size: flatParams.page_size,
      ordering: flatParams.ordering,
      billing_profile__id__in: flatParams.profile_ids,
      issue_date__range: flatParams.date_range,
      status: flatParams.status || "OPEN, PAID",
    }
  );

  useEffect(() => {
    if (data?.filters) {
      data.filters.forEach((filter) => {
        if (filter.key === "billing_profile__id__in") {
          setProfileNameOptions(
            filter.options.map((option) => {
              return {
                id: option.key,
                name: option.label,
              };
            })
          );
        }
      });
    }
  }, [data]);

  const formatInvoiceTotal = (input: {
    total: string | null;
    currency: Schemas["CurrencyEnum"];
  }) => {
    return formatNullableCurrency(input.total, {
      currency: input.currency,
    });
  };

  const invoiceColumns = [
    {
      name: t("Invoice #"),
      key: "number",
      testID: "invoice-number-header",
      isRowHeader: true,
      allowsSorting: true,
      render(item: Invoice) {
        return (
          <RouterLink
            to={item.id}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("Open Invoice {{number}}", { number: item.number })}
            data-testid="billing-invoice-button"
            className={styles.invoiceLink}
          >
            <div className={styles.generatedDateCell}>
              <div className={styles.invoiceIcon}>
                <Icon name="description" size="space-6" />
              </div>
              <div className={styles.generatedDateCellColumns}>
                <Text style="global/heading-medium">{item.number}</Text>
              </div>
            </div>
          </RouterLink>
        );
      },
    },
    {
      name: t("Billing profile"),
      key: "billing_profile__profile_name",
      isRowHeader: true,
      testID: "billing-profile-header",
      render(item: Invoice) {
        return item.billing_profile?.profile_name || "-";
      },
    },
    ...(canViewClientPortalPricing
      ? [
          {
            name: t("Total"),
            key: "total",
            isRowHeader: true,
            testID: "total-header",
            render(item: Invoice) {
              return formatInvoiceTotal({
                total: item.total,
                currency: item.currency,
              });
            },
          },
        ]
      : []),
    {
      name: t("Issue date"),
      key: "issue_date",
      isRowHeader: true,
      allowsSorting: true,
      testID: "issue-date-header",
      render(item: Invoice) {
        return formatDate(item.issue_date, "PP");
      },
    },
    {
      name: t("Status"),
      key: "status",
      isRowHeader: true,
      allowsSorting: true,
      testID: "status-header",
      render(item: Invoice) {
        return getInvoiceLabel(item.status);
      },
    },
    {
      name: t("Actions"),
      key: "actions",
      isAction: true,
      render(invoice: Invoice) {
        return <InvoicesActionsDropdownMenu invoiceId={invoice.id} />;
      },
    },
  ];

  const getInvoiceLabel = (status: string | null) => {
    switch (status) {
      case INVOICE_STATUS.draft:
        return <DefaultTag key={INVOICE_STATUS.draft}>{t("Draft")}</DefaultTag>;
      case INVOICE_STATUS.open:
        return <DefaultTag key={INVOICE_STATUS.open}>{t("Open")}</DefaultTag>;
      case INVOICE_STATUS.paid:
        return <SuccessTag key={INVOICE_STATUS.paid}>{t("Paid")}</SuccessTag>;
      case INVOICE_STATUS.voided:
        return <DangerTag key={INVOICE_STATUS.voided}>{t("Voided")}</DangerTag>;
      case INVOICE_STATUS.failed_generation:
        return (
          <DangerTag key={INVOICE_STATUS.failed_generation}>
            {t("Failed Generation")}
          </DangerTag>
        );
      default:
        return "-";
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <MediaQuery sm type="hide">
        <Stack orientation="horizontal" gap="space-2">
          <Text color="text-weak">
            <Trans>View the status and details of your current invoices.</Trans>
          </Text>
          <Link
            target="_blank"
            href="https://portal.certn.co/hc/en-us/articles/29007204255507-Billing-profiles-invoices-and-payments#h_01HWXFX3M45E8M87CD8ARRRKHZ"
            textStyle="global/body-secondary"
            linkStyle="monochrome"
            testID="billing-profile-learn-more-link"
          >
            <Trans>Learn more</Trans>
          </Link>
        </Stack>
      </MediaQuery>
      <div className={styles.filters}>
        <ProfileNameFilter
          aria-label={t("Profile name")}
          profileNameOptions={profileNameOptions}
        />
        <DateRangeFilter
          label={t("Issue date")}
          testID="issue-date-filter"
          aria-label={t("Issue date")}
        />
        <InvoiceStatusFilter />
        <ClearFilters />
      </div>
      <MediaQuery sm type="hide">
        <DataTable
          aria-label="invoice-table"
          columns={invoiceColumns}
          rows={data?.results || []}
          onSortChange={(sortDescriptor) =>
            setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
          }
          sortDescriptor={orderingParamToSortDescriptor(flatParams.ordering)}
        />
      </MediaQuery>
      <MediaQuery sm type="show">
        <Container layout="full-width">
          {!isLoading ? (
            data?.results &&
            data.results.map((invoice, i) => {
              return (
                <TableDataCard
                  invoiceNumber={invoice.number}
                  profileName={invoice.billing_profile?.profile_name || "-"}
                  total={
                    canViewClientPortalPricing
                      ? formatInvoiceTotal({
                          total: invoice.total,
                          currency: invoice.currency,
                        })
                      : ""
                  }
                  issueDate={invoice.issue_date || "-"}
                  status={invoice.status || "-"}
                  id={invoice.id}
                  key={i}
                />
              );
            })
          ) : (
            <Loader />
          )}
        </Container>
      </MediaQuery>
      <Pagination
        total={data?.count ? data.count : 0}
        page={flatParams["page"] ? Number(flatParams["page"]) : 1}
        pageCount={
          flatParams["page_size"] ? Number(flatParams["page_size"]) : 10
        }
      />
    </>
  );
}

const styles = {
  invoiceLink: css({
    textDecoration: "none",
    color: "inherit",
  }),
  filters: css({
    display: "flex",
    rowGap: "var(--space-3)",
    columnGap: "var(--space-2)",
    flexWrap: "wrap",
    flexShrink: 1,
    flexGrow: 1,
    alignItems: "flex-start",
  }),
  generatedDateCell: css({
    ":hover": {
      cursor: "pointer",
    },
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: "100%",
  }),
  generatedDateCellColumns: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  }),
  invoiceIcon: css({
    marginRight: "10px",
    flexDirection: "column",
  }),
};
